/**
 * Geoset import constants. Client-side checks are a fast-fail UX only — the
 * server (the Temporal validate workflow, services MBL-2028) is the source of
 * truth. Keep the copy in sync with the backend contract:
 *
 *   - CSV: `external_id`, `name`, `latitude`, `longitude`, `radius` are the
 *     reserved columns (case-insensitive). Every other column becomes a fence
 *     attribute; an empty cell means the attribute is absent for that row.
 *   - GeoJSON: RFC 7946 FeatureCollection of Point features (`[lng, lat]`),
 *     `properties.radius` in meters required. Accepted by the picker now;
 *     server-side parsing lands with MBL-2041 — until then the server fails
 *     validation of `.geojson` uploads with its own message.
 *   - Validation skips invalid rows (the UI opts in via
 *     `geoset_skip_invalid_rows`, MBL-2044): a mixed file reaches review with
 *     its valid rows, and the review step shows the skipped count + errors
 *     before the user confirms — a skipped row's fence is removed under
 *     replace mode, so the choice must be informed. A file with NO valid
 *     rows (or structural errors) still fails validation outright.
 */

/** Geoset import shares the geofence create permission. */
export const GEOSET_IMPORT_PERMISSION = 'journeys/geofences/create';

export const GEOSET_ACCEPTED_FILE_EXTENSIONS = ['csv', 'json', 'geojson'] as const;

/** Dropzone `accept` map (extension + mime). GeoJSON has no registered mime. */
export const GEOSET_ACCEPT: Record<string, string[]> = {
  'text/csv': ['.csv'],
  'application/json': ['.json', '.geojson'],
  'application/geo+json': ['.geojson'],
};

/** Max upload size — mirrors the server's pre-download HEAD check (64 MB). */
export const GEOSET_MAX_FILE_SIZE_BYTES = 64 * 1024 * 1024;
export const GEOSET_MAX_FILE_SIZE_MB = Math.round(GEOSET_MAX_FILE_SIZE_BYTES / 1024 / 1024);

/** Product cap on fences per geoset, enforced server-side at validation. */
export const GEOSET_FENCE_CAP = 10_000;

/** Minimum fence radius in meters (product rule, enforced server-side). */
export const GEOSET_MIN_RADIUS_METERS = 100;

/** CSV example shown in the format reference. Extra columns become attributes. */
export const GEOSET_CSV_EXAMPLE = [
  'external_id,name,latitude,longitude,radius,promo_code',
  'store-001,Mission St,37.7599,-122.4148,150,MISSION15',
  'store-002,Ferry Building,37.7955,-122.3937,200,',
  '',
].join('\n');

/** GeoJSON example shown in the format reference and offered as the sample download (coordinates are [lng, lat]). */
export const GEOSET_GEOJSON_EXAMPLE = JSON.stringify(
  {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        id: 'store-001',
        geometry: { type: 'Point', coordinates: [-122.4148, 37.7599] },
        properties: { name: 'Mission St', radius: 150, promo_code: 'MISSION15' },
      },
      {
        type: 'Feature',
        id: 'store-002',
        geometry: { type: 'Point', coordinates: [-122.3937, 37.7955] },
        properties: { name: 'Ferry Building', radius: 200 },
      },
    ],
  },
  null,
  2,
);

/** Filenames for the downloadable samples. */
export const GEOSET_CSV_SAMPLE_FILENAME = 'geofence-set-import-sample.csv';
export const GEOSET_GEOJSON_SAMPLE_FILENAME = 'geofence-set-import-sample.geojson';
